import { MessageItem } from '@/services/chat';
import { Chat } from '@/stores/slices/chat/chat.types';
import { MessageType } from 'react-chat-elements';

const avatarRandom =
  'https://ia801308.us.archive.org/8/items/whatsapp-smiling-guy-i-accidentally-made/whatsapp%20generic%20person%20light.jpg';

/**
 * Maps an array of backend message responses (MessageItem[]) to UI message props (MessageType[]).
 *
 * @param {MessageItem[]} messages - The array of message objects from the backend.
 * @param {Chat} chat - The chat object containing chat metadata for mapping.
 * @returns {MessageType[]} The mapped array of messages for UI consumption.
 */
export const mapMessagesResponseToUIProps = (
  messages: MessageItem[],
  chat: Chat
): MessageType[] => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const textMessageFiles: MessageType[] = [];

  // TODO: Check why mappedMessages is not recognized as MessageType[]
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const mappedMessages: MessageType[] = messages.map((message) => {
    const baseMessage = {
      id: message.data.message_id,
      position: message.direction === 1 ? 'left' : 'right',
      date: new Date(message.created_at),
      status: message.status,
      title: message.direction === 1 ? chat.title : 'You',
      avatar: avatarRandom
    };

    const msgReply = getMessageById(messages, message.data.context?.message_id);

    switch (message.type) {
      case 'text':
        return {
          ...baseMessage,
          type: 'text',
          text: message.data.text?.body,
          alt: message.data.text?.body || 'Message',
          reply: msgReply
            ? {
                title: msgReply.direction === 1 ? chat.title : 'You',
                message: msgReply.data.text?.body || 'No text'
              }
            : undefined
        };

      case 'image':
        return {
          ...baseMessage,
          type: 'photo',
          text: message.data.image?.caption,
          data: {
            uri: message.data.image?.url,
            status: {
              click: true,
              loading: 0.5,
              download: true,
              error: false
            },
            width: 300,
            height: 200,
            alt: message.data.image?.caption || 'Image'
          }
        };

      case 'audio':
        return {
          ...baseMessage,
          type: 'audio',
          data: {
            audioURL: message.data.audio?.url,
            audioType: 'audio/mp3',
            controlsList: 'nodownload'
          }
        };

      case 'video':
        return {
          ...baseMessage,
          type: 'video',
          controlsList: '',
          text: message.data.video?.caption,
          data: {
            uri: message.data.video?.url,
            videoURL: message.data.video?.url,
            extension: message.data.video?.mime_type,
            status: {
              click: true,
              loading: 1,
              download: true,
              error: false
            },
            width: 300,
            height: 200
          }
        };

      case 'document':
        textMessageFiles.push({
          ...baseMessage,
          type: 'text',
          text: message.data.document?.caption || 'Document',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          alt: message.data.document?.caption || 'Message',
          reply: undefined
        });
        return {
          ...baseMessage,
          type: 'file',
          text: message.data.document?.filename,
          data: {
            name: message.data.document?.filename || 'Document',
            extension: message.data.document?.mime_type,
            id: message.data.document?.id,
            uri: message.data.document?.url,
            status: {
              autoDownload: true,
              click: () => {
                window.open(message.data.document?.url, '_blank');
              }
            }
          }
        };

      default:
        return {
          ...baseMessage,
          type: 'text',
          text: 'Unsupported message type',
          alt: 'Unsupported message type'
        };
    }
  });

  return [...mappedMessages, ...textMessageFiles];
};

export const getMessageById = (
  messages: MessageItem[],
  messageId: string
): MessageItem | undefined => {
  return messages.find((message) => message.data.message_id === messageId);
};
